import { LatLng } from './types';

// Why geolocation failed, in the buckets the UI explains differently: the
// browser has the site blocked (or the user refused the prompt), the page is
// not a secure context so the API is refused before any prompt (plain http,
// e.g. local dev), or anything else (no API, timeout, no fix).
export type GeolocationFailure = 'denied' | 'insecure' | 'unavailable';

// Coerce a rejection from getCurrentPosition() (or anything thrown around it)
// into a GeolocationFailure so consumers can pick the right copy.
export const toGeolocationFailure = (err: unknown): GeolocationFailure => {
  if (err === 'denied' || err === 'insecure') return err;
  return 'unavailable';
};

// Read the device position once with the options every consumer used to pass
// inline (high accuracy, 10 s cap). Rejects with a GeolocationFailure.
export const getCurrentPosition = (): Promise<LatLng> =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject('unavailable');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      (geoError) => {
        if (geoError.code !== geoError.PERMISSION_DENIED) {
          reject('unavailable');
          return;
        }
        // Browsers report an insecure origin as a permission denial too; that
        // one cannot be fixed in site settings, so keep it apart.
        reject(window.isSecureContext ? 'denied' : 'insecure');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  });
